import React, { Fragment, useState, useReducer, useEffect } from 'react'
import { initialState, reducer } from './reducer'
import styled from 'styled-components'
import { navigate, useLocation } from '@reach/router'

import Loader from '../../shared/Loader'
import ContentWrapper from '../../shared/ContentWrapper'
import PageDescription from '../../shared/PageDescription'
import StructuredTable from '../../shared/StructuredTable'
import { APP_STATE, MAX_CONTENT_WIDTH, RED } from '../../../constants'
import { SECONDARY_BUTTON } from '../../../constants/buttonThemes'
import Button from '../../shared/Button'
import Dropdown from '../../shared/Dropdown'
import Text from '../../shared/Text'
import RuleInfoDialog from '../../shared/FeedAuditorComponents/RuleInfoDialog'
import downloadFile from '../../../lib/downloadFile'
import Tooltip from '../../shared/Tooltip2'
import {
  getAuditLabelById,
  getProductFailureDetailsById,
  receivedAuditLabelData,
  getAuditRulesById,
  getFeedWithFailuresProgress,
  downloadFeedWithFailuresFile,
  generateFeedWithFailures,
} from './actions'

const StyledButton = styled(Button)`
  height: 35px;
  white-space: nowrap;
`

const RowRadius = styled.div`
  .ReactTable .rt-tbody .rt-tr-group {
    border-radius: 5px;
  }
`

const Spacer = styled.div`
  padding-top: 10px;
`

const ButtonWrapper = styled.div`
  display: flex;
  margin: auto 0 0;
  gap: 15px;
  max-width: ${MAX_CONTENT_WIDTH};
`

const OverflowWrapper = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`

const RuleFilterWrapper = styled.div`
  width: 380px;
  height: 55px;
  display: flex;
  gap: 10px
  align-items: center;
`

const ProductFailureDetails = ({ t, organisationId, feedAuditId }) => {
  const location = useLocation()
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    auditLabelState,
    auditLabelData,
    productFailureDetailsState,
    productFailureDetailsData,
    pageTotal,
    page,
    auditRulesState,
    auditRulesData,
    feedWithFailuresProgressState,
    feedWithFailuresProgressData,
    generateFeedWithFailuresState,
  } = state
  const [limit, setLimit] = useState(10) // i.e. page size for pagination
  const [ruleFilter, setRuleFilter] = useState(
    '00000000-0000-0000-0000-000000000000'
  )
  const [ruleInfoDialogVisible, setRuleInfoDialogVisible] = useState(false)
  const [hasPageBeenRendered, setHasPageBeenRendered] = useState(false)

  useEffect(() => {
    GetAuditLabel()
    getProductFailureDetailsById(feedAuditId, dispatch, page, limit, ruleFilter)
    getAuditRulesById(dispatch)
    getFeedWithFailuresProgress(feedAuditId, dispatch)
  }, [])

  useEffect(() => {}, [
    feedWithFailuresProgressState,
    generateFeedWithFailuresState,
  ])

  useEffect(
    () => {
      // Don't want to re-trigger the data being requested on first render
      if (!hasPageBeenRendered) {
        setHasPageBeenRendered(true)
        return
      }

      getProductFailureDetailsById(feedAuditId, dispatch, 0, limit, ruleFilter)
    },
    [limit, ruleFilter]
  )

  // Only need ruleId and name for the dropdown, and no repeats
  const simpleRules = auditRulesData.map(item => ({
    id: item.id,
    name: item.name,
  }))
  let distinctRules = [
    ...new Map(simpleRules.map(item => [item['id'], item])).values(),
  ]

  // Add option to select all rules for the dropdown
  const ruleDropdownOptions = [
    { id: '00000000-0000-0000-0000-000000000000', name: t('all_rules') },
  ].concat(distinctRules)

  function GetAuditLabel() {
    // Pass auditLabel through from main audit page (with useLocation) to save getting it from the api
    if (location.state.auditLabel) {
      dispatch(receivedAuditLabelData(location.state.auditLabel))
    } else {
      // If the page is refreshed / not navigated from main audit page, we will need to get the label from the api
      getAuditLabelById(feedAuditId, dispatch)
    }
  }

  function IsEntirePageDataLoading() {
    return (
      (!auditLabelState ||
        auditLabelState === APP_STATE.FETCHING ||
        !feedWithFailuresProgressState ||
        feedWithFailuresProgressState === APP_STATE.FETCHING) &&
      (!productFailureDetailsState ||
        productFailureDetailsState === APP_STATE.FETCHING ||
        !auditRulesState ||
        auditRulesState === APP_STATE.FETCHING)
    )
  }

  function IsHeaderDataDoneWithTableDataLoading() {
    return (
      auditLabelState === APP_STATE.DONE &&
      feedWithFailuresProgressState === APP_STATE.DONE &&
      (!productFailureDetailsState ||
        productFailureDetailsState === APP_STATE.FETCHING ||
        !auditRulesState ||
        auditRulesState === APP_STATE.FETCHING)
    )
  }

  function IsAnyPageDataErrored() {
    return (
      auditLabelState === APP_STATE.ERROR ||
      productFailureDetailsState === APP_STATE.ERROR ||
      auditRulesState === APP_STATE.ERROR ||
      feedWithFailuresProgressState === APP_STATE.ERROR ||
      generateFeedWithFailuresState === APP_STATE.ERROR
    )
  }

  function ShowRuleFiltering() {
    return (
      <RuleFilterWrapper>
        <Dropdown
          label={t('filter_by_rule')}
          onChange={event => {
            setRuleFilter(event.id)
          }}
          options={ruleDropdownOptions}
          boundValue={{
            id: ruleFilter,
            name: ruleDropdownOptions.find(r => r.id === ruleFilter).name,
          }}
          isHigh
        />
        <StyledButton
          dataId={`rule-info`}
          key={'rule-info'}
          theme={SECONDARY_BUTTON}
          onClick={() => setRuleInfoDialogVisible(true)}
        >
          {t('rule_info')}
        </StyledButton>
      </RuleFilterWrapper>
    )
  }

  const TableColumns = t => [
    {
      Header: t('failed_rule'),
      accessor: 'rule',
      Cell: row => {
        return (
          <OverflowWrapper>
            {auditRulesData.find(r => r.id === row.original.rule).name}
          </OverflowWrapper>
        )
      },
      maxWidth: 300,
    },
    {
      Header: t('partner_name'),
      accessor: 'partnerName',
      Cell: row => {
        return <OverflowWrapper>{row.original.partnerName}</OverflowWrapper>
      },
      maxWidth: 200,
    },
    {
      Header: t('product_title'),
      accessor: 'productTitle',
      Cell: row => {
        return <OverflowWrapper>{row.original.productTitle}</OverflowWrapper>
      },
    },
    {
      Header: t('product_gtin'),
      accessor: 'productGTIN',
      Cell: row => {
        return <OverflowWrapper>{row.original.productGTIN}</OverflowWrapper>
      },
      maxWidth: 200,
    },
    {
      Header: t('product_mpn'),
      accessor: 'productMPN',
      Cell: row => {
        return <OverflowWrapper>{row.original.productMPN}</OverflowWrapper>
      },
      maxWidth: 200,
    },
    {
      Header: t('file_row'),
      accessor: 'rowNumber',
      Cell: row => {
        // File row numbers are stored in DB -1 so the header is row 0, and the first product row is 1. It will make more sense to the user to +1 here
        return <OverflowWrapper>{row.original.rowNumber + 1}</OverflowWrapper>
      },
      width: 130,
    },
  ]

  function ShowPageHeader() {
    return (
      <div>
        <PageDescription
          heading={t('product_failure_heading')}
          description={
            t('product_failure_description') + '"' + auditLabelData + '"'
          }
        />
        <ButtonWrapper>
          <StyledButton
            dataId={`back`}
            key={'back'}
            theme={SECONDARY_BUTTON}
            onClick={() => navigate(`/${organisationId}/admin/feed_audit`)}
          >
            {t('button_back')}
          </StyledButton>
          <Tooltip title={t('download_failures_tooltip')}>
            <StyledButton
              dataId={`download`}
              key={'download'}
              theme={SECONDARY_BUTTON}
              onClick={() =>
                downloadFile(
                  t,
                  `feedaudit/product-failure/${feedAuditId}/download`,
                  auditLabelData + '-product-failures',
                  'csv'
                )
              }
            >
              {t('download_failures')}
            </StyledButton>
          </Tooltip>
          {ShowFeedWithFailuresButton()}
        </ButtonWrapper>
      </div>
    )
  }

  function ShowFeedWithFailuresButton() {
    if (
      feedWithFailuresProgressState === APP_STATE.DONE &&
      generateFeedWithFailuresState !== APP_STATE.FETCHING
    ) {
      switch (feedWithFailuresProgressData) {
        case -1: // Original feed audit file does not exist
          return (
            <Tooltip title={t('download_failures_unavailable')}>
              <StyledButton
                dataId={`download-with-feed`}
                key={'download-with-feed'}
                theme={SECONDARY_BUTTON}
                disabled
              >
                {t('generate_feed_with_failures')}
              </StyledButton>
            </Tooltip>
          )
        case 0: // File has not been requested yet
          return (
            <Tooltip title={t('generate_feed_with_failures_tooltip')}>
              <StyledButton
                dataId={`download-with-feed`}
                key={'download-with-feed'}
                theme={SECONDARY_BUTTON}
                onClick={() => {
                  generateFeedWithFailures(t, feedAuditId, dispatch)
                }}
              >
                {t('generate_feed_with_failures')}
              </StyledButton>
            </Tooltip>
          )
        case 1: // File generation is in progress
        case 2: // File generation is completed
          return (
            <Tooltip title={t('download_feed_with_failures_tooltip')}>
              <StyledButton
                dataId={`download-with-feed`}
                key={'download-with-feed'}
                theme={SECONDARY_BUTTON}
                onClick={() => {
                  downloadFeedWithFailuresFile(t, feedAuditId, auditLabelData)
                }}
              >
                {t('download_feed_with_failures')}
              </StyledButton>
            </Tooltip>
          )
        default:
          return
      }
    } else {
      // Fetching state
      return (
        <StyledButton
          dataId={`download-with-feed`}
          key={'download-with-feed'}
          theme={SECONDARY_BUTTON}
          disabled
        >
          {t('generate_feed_with_failures')}
        </StyledButton>
      )
    }
  }

  if (IsEntirePageDataLoading()) {
    return <Loader />
  } else if (IsAnyPageDataErrored()) {
    return (
      <Text color={RED} bold>
        {t('feed_audit_modal_data_failed')}
      </Text>
    )
  } else if (IsHeaderDataDoneWithTableDataLoading()) {
    return (
      <div>
        {ShowPageHeader()}
        <Spacer />
        <Loader />
      </div>
    )
  }

  return (
    <Fragment>
      <RuleInfoDialog
        visible={ruleInfoDialogVisible}
        handleClose={() => {
          setRuleInfoDialogVisible(false)
        }}
        ruleData={auditRulesData}
        t={t}
      />
      {ShowPageHeader()}
      <Spacer />
      <ContentWrapper>
        <RowRadius>
          <StructuredTable
            columns={TableColumns(t)}
            data={productFailureDetailsData}
            page={page}
            pageSize={limit}
            pageTotal={pageTotal}
            selectNextPage={() =>
              getProductFailureDetailsById(
                feedAuditId,
                dispatch,
                page + 1,
                limit,
                ruleFilter
              )
            }
            selectPreviousPage={() =>
              getProductFailureDetailsById(
                feedAuditId,
                dispatch,
                page - 1,
                limit,
                ruleFilter
              )
            }
            showPagination={true}
            sortable={false}
            setPageSize={setLimit}
            rowPadding
            displayTopContent={ShowRuleFiltering}
            t={t}
          />
        </RowRadius>
      </ContentWrapper>
    </Fragment>
  )
}

export default ProductFailureDetails
